import { Button } from "@/components/design-system/button";
import { Input } from "@/components/design-system/input";
import Modal from "@/components/design-system/Modal";
import PreviewFileModal from "@/components/design-system/PreviewFileModal";
import SelectWithCheckbox from "@/components/design-system/SelectWithCheckbox";
import SelectWithRadio from "@/components/design-system/SelectWithRadio";
import {
  getAssignResourceDropdownV2Service,
  getLessonsDropdownService,
  getUnitsDropdownService,
  uploadFileService,
  type AssignResourceType,
} from "@/service/base.service";
import { getAllStudentV2Service } from "@/service/student.service";
import { AppDispatch, RootState } from "@/store";
import {
  selectAssignmentActionLoading,
  updateAssignment,
} from "@/store/slices/assignmentSlice";
import { AssignmentFormData } from "@/types/assignment.model";
import type { StudentListType } from "@/types/student.model";
import { getLessonDropdownQuery } from "@/utils/assignmentRecordParams";
import { getFileConfig } from "@/utils/constants";
import { Play } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

interface EditAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  assignment: AssignmentFormData | null;
}

const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({
  isOpen,
  onClose,
  onRefresh,
  assignment,
}) => {
  if (!assignment) return null;

  const dispatch = useDispatch<AppDispatch>();
  const actionLoading = useSelector(selectAssignmentActionLoading);
  const user = useSelector((state: RootState) => state.auth.user);

  const [previewModalOpen, setPreviewModalOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<{
    file_type: string;
    url: string;
    id: string;
  } | null>(null);
  const [fileType, setFileType] = useState<
    "video" | "audio" | "pdf" | "docx" | "unknown"
  >("unknown");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initialResourceType =
    (assignment.type as AssignResourceType) ?? "course";
  const initialResourceId =
    typeof assignment.grade === "string"
      ? assignment.grade
      : (assignment.grade as { id: string; name: string })?.id ?? "";

  const [studentOptions, setStudentOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [resourceOptions, setResourceOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [lessons, setLessons] = useState<{ label: string; value: string }[]>(
    []
  );
  const [units, setUnits] = useState<{ label: string; value: string }[]>([]);

  const {
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    register,
    trigger,
  } = useForm<AssignmentFormData>({
    defaultValues: {
      name: assignment.name || "",
      type: initialResourceType,
      grade: initialResourceId,
      cs_type: assignment.assign_type === "UNIT" ? "unit" : "general",
      lesson: assignment.lesson
        ? (assignment.lesson as { id: string; lesson: string }).id
        : "",
      unit: assignment.unit
        ? (assignment.unit as { id: string; unit: string }).id
        : "",
      notes: assignment.notes || "",
      students:
        assignment.students.map((student) => (student as { id: string }).id) ||
        [],
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
  });

  const initialFetchDone = useRef(false);

  const resourceType = (watch("type") as AssignResourceType) || "course";
  const resourceLabel =
    resourceType === "class" ? "Select Class" : "Select Grade";
  const resourcePlaceholder = resourceLoading
    ? "Loading…"
    : resourceType === "class"
      ? "Select class"
      : "Select grade";

  const fetchResourceOptions = async (type: AssignResourceType) => {
    setResourceLoading(true);
    try {
      const { data } = await getAssignResourceDropdownV2Service(type);
      const items = (data.data ?? []).map((item) => {
        if (type === "course" && "grade" in item) {
          return { id: item.id, label: item.grade };
        }
        if ("name" in item) {
          return { id: item.id, label: item.name };
        }
        return { id: item.id, label: item.id };
      });
      setResourceOptions(items);
    } catch (error) {
      console.error("Error fetching assign resources:", error);
      setResourceOptions([]);
    } finally {
      setResourceLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if we haven't done the initial fetch yet
    if (!initialFetchDone.current) {
      void fetchResourceOptions(initialResourceType);
      void fetchStudents(initialResourceId, initialResourceType);

      if (assignment.assign_type === "UNIT") {
        void fetchLessons(initialResourceId, initialResourceType);
      }

      // Mark that we've done the initial fetch
      initialFetchDone.current = true;
    }
  }, [assignment]);

  useEffect(() => {
    if (assignment?.uploads && assignment.uploads.length > 0) {
      setLoading(true);
      setError(null);

      try {
        // Get file extension from URL
        const fileUrl =
          typeof assignment.uploads[0] === "string"
            ? assignment.uploads[0]
            : assignment.uploads[0].url;
        const extension =
          (fileUrl as string).split(".").pop()?.toLowerCase() || "";

        // Determine file type based on extension
        if (["mp4", "webm", "ogg", "mov"].includes(extension)) {
          setFileType("video");
        } else if (["mp3", "wav", "ogg", "m4a"].includes(extension)) {
          setFileType("audio");
        } else if (extension === "pdf") {
          setFileType("pdf");
        } else if (["doc", "docx"].includes(extension)) {
          setFileType("docx");
        } else {
          setFileType("unknown");
          setError(`Unsupported file type: ${extension}`);
        }
      } catch (err) {
        console.error("Error determining file type:", err);
        setError("Error determining file type");
      } finally {
        setLoading(false);
      }
    }
  }, [assignment?.uploads]);

  const fetchStudents = async (
    resourceId?: string,
    listType: StudentListType = resourceType
  ) => {
    if (!resourceId) return;

    try {
      const { data } = await getAllStudentV2Service({
        page: 1,
        pageSize: 500,
        type: listType,
        teacherId: user?.teacher_id,
        schoolId: user?.school_id,
        ...(listType === "class"
          ? { classId: resourceId }
          : { gradeId: resourceId }),
      });

      if (data?.data) {
        setStudentOptions(
          data.data.map((student) => ({
            value: student.user_id ?? student.id,
            label: student.name,
          }))
        );
      } else {
        setStudentOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch students:", error);
      setStudentOptions([]);
    }
  };

  const fetchLessons = async (
    resourceId?: string,
    type: AssignResourceType = resourceType
  ) => {
    if (!resourceId) return;

    try {
      const { data } = await getLessonsDropdownService(
        getLessonDropdownQuery(type, resourceId)
      );

      if (data && data.data) {
        const lessonOptions = data.data.map(
          (lesson: { id: string; lesson: string }) => ({
            value: lesson.id,
            label: `${lesson.lesson}`,
          })
        );
        setLessons(lessonOptions);

        // If we have a lesson, fetch units for that lesson
        if (assignment?.lesson) {
          fetchUnits(
            typeof assignment.lesson === "object"
              ? (assignment.lesson as any).id
              : (assignment.lesson as string)
          );
        }
      }
    } catch (error) {
      console.error("Failed to fetch lessons:", error);
    }
  };

  const fetchUnits = async (lessonId?: string) => {
    try {
      const { data } = await getUnitsDropdownService({
        search: "",
        lessonId,
      });

      if (data && data.data) {
        const unitOptions = data.data.map(
          (unit: { id: string; unit: string }) => ({
            value: unit.id,
            label: `${unit.unit}`,
          })
        );
        setUnits(unitOptions);
      }
    } catch (error) {
      console.error("Failed to fetch units:", error);
    }
  };

  // Enhanced handleFileUpload function to handle both regular Files and our enhanced blob objects
  const handleFileUpload = (file: File) => {
    setValue("attachment", file, { shouldValidate: true });
  };

  const onFormSubmit = async (data: AssignmentFormData) => {
    try {
      // Check if we have any recorded audio/video files that need special handling
      const hasRecordedMedia = data.attachment;

      let payload: AssignmentFormData = {
        name: data.name,
        type: data.type ?? resourceType,
        grade: data.grade,
        students: data.students,
        notes: data.notes,
        uploads: assignment?.uploads?.map((upload) => upload.id) || [],
      };

      if (data.cs_type === "unit") {
        payload.lesson = data.lesson as string;
        payload.unit = data.unit as string;
      }

      if (hasRecordedMedia) {
        try {
          const {
            data: {
              data: { id },
            },
          } = await uploadFileService(hasRecordedMedia);
          payload.uploads = [id];
        } catch (error) {
          console.error("Failed to upload file:", error);
          return;
        }
      }

      if (assignment && assignment?.id) {
        await dispatch(
          updateAssignment({ id: assignment?.id, assignmentData: payload })
        )
          .unwrap()
          .then(() => {
            onRefresh();
            onClose();
          });
      }
    } catch (error: any) {
      console.error("Error submitting form:", error.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Assignment"
      width="896px"
      contentClassName="p-0"
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className="p-6 flex flex-col max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {/* Assignment Type */}
            {/* <div>
              <SelectWithRadio
                id="cs_type"
                label="Assignment Type"
                placeholder="Select assignment type"
                options={[
                  { value: "general", label: "General Assignment" },
                  { value: "unit", label: "Unit Assignment" },
                ]}
                value={watch("cs_type") || ""}
                onChange={(value) => {
                  const newType = value as "general" | "unit";
                  setValue("cs_type", newType, {
                    shouldValidate: true,
                  });

                  if (newType === "general") {
                    setValue("lesson", "");
                    setValue("unit", "");
                  }
                }}
                error={errors.cs_type?.message as string}
                required={true}
                showAbove={false}
                maxHeight="300px"
              />
            </div> */}

            {/* Assignment name */}
            <div>
              <Input
                id="name"
                label="Assignment name"
                {...register("name", {
                  required: "Assignment name is required",
                })}
                placeholder="Enter name"
                required={true}
                maxLength={60}
                errorMessage={errors.name?.message as string}
              />
            </div>

            {/* Resource type (read-only on edit) */}
            <div>
              <SelectWithRadio
                id="resourceType"
                label="Type"
                placeholder="Select type"
                options={[
                  { value: "course", label: "Course" },
                  { value: "class", label: "Class" },
                ]}
                value={resourceType}
                onChange={() => undefined}
                disabled={true}
                showAbove={false}
                maxHeight="300px"
              />
            </div>

            {/* Select course / class */}
            <div>
              <SelectWithRadio
                id="grade"
                label={resourceLabel}
                placeholder={resourcePlaceholder}
                options={resourceOptions.map((item) => ({
                  value: item.id,
                  label: item.label,
                }))}
                disabled={resourceLoading}
                value={(watch("grade") as string) || ""}
                onChange={(value) => {
                  setValue("grade", value, { shouldValidate: true });
                  fetchStudents(value, resourceType);
                  setValue("students", []);
                  trigger("grade");

                  if (watch("cs_type") === "unit") {
                    fetchLessons(value, resourceType);
                    setValue("lesson", "");
                    setValue("unit", "");
                  }
                }}
                error={errors.grade?.message as string}
                showAbove={false}
                maxHeight="300px"
              />
            </div>

            {/* Students */}
            <div>
              <SelectWithCheckbox
                id="studentIds"
                label="Students"
                placeholder="Select student"
                options={studentOptions}
                value={(watch("students") as string[]) || []}
                onChange={(value) =>
                  setValue("students", value, { shouldValidate: true })
                }
                error={errors.students?.message as string}
                showAbove={false}
                maxHeight="300px"
              />
            </div>

            {/* Unit Assignment specific fields */}
            {watch("cs_type") === "unit" && (
              <>
                <div>
                  <SelectWithRadio
                    id="lessonId"
                    label="Lesson Name"
                    placeholder="Select lesson"
                    options={lessons}
                    disabled={!watch("grade")}
                    value={(watch("lesson") as string) || ""}
                    onChange={(value) => {
                      setValue("lesson", value, { shouldValidate: true });
                      trigger("lesson");

                      fetchUnits(value);
                      setValue("unit", "");
                    }}
                    error={errors.lesson?.message as string}
                    showAbove={false}
                    maxHeight="300px"
                  />
                  <input
                    type="hidden"
                    {...register("lesson", {
                      required:
                        watch("cs_type") === "unit"
                          ? "Lesson is required"
                          : false,
                    })}
                  />
                </div>
                <div>
                  <SelectWithRadio
                    id="unitId"
                    label="Unit Name"
                    placeholder="Select unit"
                    options={units}
                    disabled={!watch("lesson")}
                    value={(watch("unit") as string) || ""}
                    onChange={(value) => {
                      setValue("unit", value, { shouldValidate: true });
                      trigger("unit");
                    }}
                    error={errors.unit?.message as string}
                    showAbove={true}
                    maxHeight="300px"
                  />
                  <input
                    type="hidden"
                    {...register("unit", {
                      required:
                        watch("cs_type") === "unit"
                          ? "Unit is required"
                          : false,
                    })}
                  />
                </div>
              </>
            )}
          </div>

          <div className="w-full h-px bg-[#E2E8F0] mt-[32px] mb-[24px]"></div>

          {/* File Uploaded Section */}
          {assignment.uploads && assignment.uploads.length > 0 ? (
            <div className="mb-8">
              <h2 className="text-[#1F1F1F] text-[20px] font-bold mb-6">
                File Uploaded
              </h2>
              <div className="flex flex-row gap-2">
                {assignment.uploads?.map((file, index) => (
                  <div key={index} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(
                          file as {
                            file_type: string;
                            url: string;
                            id: string;
                          }
                        );
                        setPreviewModalOpen(true);
                      }}
                      className="flex items-center justify-between w-[200px] gap-2 text-[#5C88C5] py-2 px-4 border border-[#5C88C5] rounded-xl"
                    >
                      <div className="flex gap-1">
                        {
                          getFileConfig(
                            file as {
                              file_type: string;
                              url: string;
                              id: string;
                            }
                          ).icon
                        }

                        <span className="text-sm truncate max-w-[100px]">
                          {
                            getFileConfig(
                              file as {
                                file_type: string;
                                url: string;
                                id: string;
                              }
                            ).fileType
                          }
                        </span>
                      </div>
                      <Play className="w-6 h-6 text-[#5C88C5]" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="my-4">
                <p className="text-base font-medium text-[#2D3748] mb-2">
                  Write a note
                </p>
                <textarea
                  className="w-full border border-gray-200 rounded-2xl p-3 min-h-[100px] text-[14px] text-[#1F1F1F] font-[500] placeholder:text-[#A0AEC0] focus:outline-none"
                  placeholder="Enter a note"
                  value={watch("notes")}
                  onChange={(e) => setValue("notes", e.target.value)}
                ></textarea>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer with buttons */}
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            disabled={!isValid || actionLoading}
            loading={actionLoading}
          >
            Update
          </Button>
        </div>
      </form>

      <PreviewFileModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fileUrl={selectedFile?.url || ""}
        fileType={selectedFile?.file_type || "unknown"}
      />
    </Modal>
  );
};

export default EditAssignmentModal;
